import NutMessageOverlay from "@/components/NutMessageOverlay";
import NutMessageToggle from "@/components/NutMessageToggle";
import ProjectIssuesSidebar from "@/components/ProjectIssuesSidebar";
import ThemeToggle from "@/components/ThemeToggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Header as HeaderPrimitive, useHeaderContext } from "@/components/ui/header";
import { SidebarTrigger } from "@/components/ui/sidebar";
import SharedCopy from "@/config/SharedCopy.json";
import { useAuth } from "@/contexts/AuthProvider";
import { cn } from "@/lib/utils";
import { Bug, Home, Inbox, LayoutGrid, LogOut, Mail, Settings, Target, User, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// Custom overflow toggle with test ID
function OverflowToggle() {
  const { overflowCount } = useHeaderContext();
  if (overflowCount <= 0) return null;

  return (
    <SidebarTrigger
      className="inline-flex items-center justify-center rounded-md border px-2.5 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label="Open overflow menu"
      data-testid="sidebar-toggle"
    >
      <span aria-hidden>⋯</span>
    </SidebarTrigger>
  );
}

// App branding logo
function AppLogo() {
  return (
    <Link
      to="/"
      className="flex items-center gap-2 mr-4 hover:opacity-80 transition-opacity"
      data-testid="header-logo-link"
    >
      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-primary-foreground shadow-sm">
        <Bug className="h-4 w-4" />
      </div>
      <span className="font-semibold text-foreground hidden sm:inline-block">IssueTracker</span>
    </Link>
  );
}

// Navigation items are either links or sidebar content.
export const navItems: Array<
  | {
      path: string;
      label: string;
      icon: React.ElementType | undefined;
      ariaLabel?: string;
    }
  | { sidebarContent: React.ElementType }
> = [
  { path: "/", label: "Home", icon: Home },
  { path: "/inbox", label: "Inbox", icon: Mail },
  { path: "/my-issues", label: "My Issues", icon: User },
  { path: "/initiatives", label: "Initiatives", icon: Target },
  { path: "/roadmap", label: "Roadmap", icon: LayoutGrid },
  { path: "/team", label: "Team", icon: Users },
  { path: "/triage", label: "Triage", icon: Inbox },
  { sidebarContent: ProjectIssuesSidebar },
];

function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return null;
  }

  const fullName = user.name || "(unnamed user)";
  const avatarUrl = user.avatar_url;
  const userInitials = fullName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const handleAccountSettings = () => {
    navigate("/profile");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 rounded-full ring-2 ring-transparent hover:ring-primary/20 transition-all"
          data-testid="user-menu-trigger"
        >
          <Avatar className="h-9 w-9">
            <AvatarImage src={avatarUrl} alt={fullName} />
            <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
              {userInitials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-content">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{fullName}</p>
            <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleAccountSettings} data-testid="user-menu-account-settings">
          <Settings className="mr-2 h-4 w-4" />
          <span>Account Settings</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          className="text-destructive focus:text-destructive focus:bg-destructive/10"
          data-testid="user-menu-sign-out"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Header({ className }: { className?: string }) {
  const location = useLocation();
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // Set the document title from config
  useEffect(() => {
    const appName = SharedCopy["app.name"]?.text;
    if (appName) {
      document.title = appName;
    }
  }, []);

  // When there is content to render in the sidebar, we force it to always render.
  const hasSidebarContent = navItems.some((item) => "sidebarContent" in item);

  return (
    <>
      <HeaderPrimitive
        className={cn(
          "sticky top-0 z-50 border-b bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80",
          className
        )}
        hasSidebarContent={hasSidebarContent}
      >
        <HeaderPrimitive.Left>
          <AppLogo />
          <OverflowToggle />
        </HeaderPrimitive.Left>
        <HeaderPrimitive.Center>
          {navItems.map((item, index) => {
            if ("sidebarContent" in item) {
              return (
                <HeaderPrimitive.SidebarContent key={index}>
                  <item.sidebarContent />
                </HeaderPrimitive.SidebarContent>
              );
            }
            const Icon = item.icon;
            // Mark as active if the current path matches
            const isActive = location.pathname === item.path;
            return (
              <HeaderPrimitive.Link
                key={item.path}
                to={item.path}
                aria-label={item.ariaLabel || item.label}
                className={cn(
                  "relative flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-sm font-medium",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )}
                data-testid={`nav-${item.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")
                  .replace(/[\(\)]/g, "")}`}
                aria-current={isActive ? "page" : undefined}
              >
                {Icon && <Icon aria-hidden className="h-4 w-4" />}
                <span>{item.label}</span>
                {isActive && (
                  <span className="absolute -bottom-[9px] left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full" />
                )}
              </HeaderPrimitive.Link>
            );
          })}
        </HeaderPrimitive.Center>
        <HeaderPrimitive.Right>
          <div className="flex items-center gap-1">
            <NutMessageToggle onClick={() => setIsOverlayOpen(true)} />
            <ThemeToggle />
            <div className="w-px h-6 bg-border mx-2" />
            <UserMenu />
          </div>
        </HeaderPrimitive.Right>
      </HeaderPrimitive>

      <NutMessageOverlay isOpen={isOverlayOpen} onClose={() => setIsOverlayOpen(false)} />
    </>
  );
}

export default Header;
